import { showDialog } from '@/utils/dialogs';
/**
 * VoiceUploadButton
 * 
 * A mic button for the chat header that opens an action sheet with voice transcription options:
 * - Upload Audio File
 * - Record Audio (mobile only)
 * - View Transcripts
 */

import React from 'react';
import { TouchableOpacity, ActivityIndicator, StyleProp, ViewStyle } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import * as DocumentPicker from 'expo-document-picker';
import { useTranslation } from '../../hooks/useTranslation';
import { AUDIO_UPLOAD_MIME_TYPES } from '../../utils/audioUploadFormats';


// File size limits (matching backend AUDIO_MAX_SIZE_MB)
const MAX_FILE_SIZE_MB = 100;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export interface VoiceUploadButtonProps {
    /** Called when user selects an audio file */
    onFileSelected: (file: { uri: string; type: string; name: string; size?: number }) => void;
    /** Whether a transcription is currently in progress */
    loading?: boolean;
    /** Whether the button should be disabled (e.g., quota exceeded) */
    disabled?: boolean;
    /** Button size */
    size?: number;
    /** Icon color */
    color?: string;
    /** Background color */
    backgroundColor?: string;
    /** Optional border color for icon-only web controls */
    borderColor?: string;
    /** Optional border width for icon-only web controls */
    borderWidth?: number;
    /** Optional outer style override */
    style?: StyleProp<ViewStyle>;
}

export function VoiceUploadButton({
    onFileSelected,
    loading = false,
    disabled = false,
    size = 36,
    color = 'white',
    backgroundColor = 'rgba(255, 255, 255, 0.2)',
    borderColor = 'transparent',
    borderWidth = 0,
    style,
}: VoiceUploadButtonProps) {
    const { t } = useTranslation();

    const pickAudioFile = async () => {
        try {
            const result = await DocumentPicker.getDocumentAsync({
                type: AUDIO_UPLOAD_MIME_TYPES,
                copyToCacheDirectory: true,
            });

            if (result.canceled || !result.assets || result.assets.length === 0) {
                return;
            }

            const asset = result.assets[0];

            // Validate file size before proceeding
            if (asset.size && asset.size > MAX_FILE_SIZE_BYTES) {
                const sizeMB = (asset.size / (1024 * 1024)).toFixed(1);
                const errorMsg = t('transcription.errors.fileTooLarge')
                    ?.replace('{{size}}', sizeMB)
                    ?.replace('{{max}}', String(MAX_FILE_SIZE_MB))
                    || `File too large: ${sizeMB}MB exceeds ${MAX_FILE_SIZE_MB}MB limit`;

                showDialog(errorMsg);
                return;
            }

            onFileSelected({
                uri: asset.uri,
                type: asset.mimeType || 'audio/mpeg',
                name: asset.name || 'audio',
                size: asset.size,
            });
        } catch (error: any) {
            console.error('Audio file pick error:', error);
            const message = t('transcription.errors.uploadInterrupted', t('transcription.errors.uploadFailed'));
            showDialog(message);
        }
    };

    const handlePress = () => {
        if (disabled || loading) return;
        pickAudioFile();
    };

    return (
        <TouchableOpacity
            onPress={handlePress}
            disabled={disabled || loading}
            style={[
                {
                    width: size,
                    height: size,
                    borderRadius: size / 2,
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor,
                    borderColor,
                    borderWidth,
                    opacity: disabled ? 0.5 : 1,
                },
                style,
            ]}
            accessibilityLabel={t('transcription.uploadAudio')}
        >
            {loading ? (
                <ActivityIndicator size="small" color={color} />
            ) : (
                <MaterialIcons name="mic" size={size * 0.55} color={color} />
            )}
        </TouchableOpacity>
    );
}

export default VoiceUploadButton;
